import type {
  Attachment,
  CliRelease,
  ConversationListResponse,
  ConversationMessagesResponse,
  CreateAttachmentRequest,
  CreateAttachmentResponse,
  CustomFlashback,
  CustomFlashbackListResponse,
  PromptCardsResponse,
  SessionReport,
} from 'src/services/ai-studio/types';
import { dashboardApi } from 'src/services/dashboard-api';

export const AI_STUDIO_STREAM_URL = (threadId: string, cursor?: string) => {
  const base = `${API_URL}ai-studio/conversations/${threadId}/stream`;
  return cursor ? `${base}?cursor=${encodeURIComponent(cursor)}` : base;
};

export const aiStudioApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    cancelAIStudioRun: builder.mutation<{ status: string }, string>({
      query: threadId => ({
        method: 'POST',
        url: `ai-studio/conversations/${threadId}/cancel`,
      }),
    }),
    clearPendingAIStudioAttachments: builder.mutation<
      { status: string },
      string
    >({
      // Fire-and-forget on conversation switch / modal close. Drops
      // unsent rows + staged S3 keys (the bucket has no lifecycle).
      query: threadId => ({
        method: 'DELETE',
        url: `ai-studio/threads/${threadId}/pending-attachments`,
      }),
    }),
    createAIStudioAttachment: builder.mutation<
      CreateAttachmentResponse,
      CreateAttachmentRequest
    >({
      query: body => ({
        body,
        method: 'POST',
        url: 'ai-studio/attachments/presigned-upload',
      }),
    }),
    createCustomFlashback: builder.mutation<CustomFlashback, string>({
      invalidatesTags: ['CustomFlashbacks'],
      query: conversationId => ({
        body: { conversation_id: conversationId },
        method: 'POST',
        url: 'ai-studio/custom-flashbacks',
      }),
    }),
    deleteAIStudioConversation: builder.mutation<{ status: string }, string>({
      invalidatesTags: ['AIStudioConversations'],
      query: threadId => ({
        method: 'DELETE',
        url: `ai-studio/conversations/${threadId}`,
      }),
    }),
    deleteCustomFlashback: builder.mutation<{ status: string }, string>({
      invalidatesTags: ['CustomFlashbacks'],
      query: flashbackId => ({
        method: 'DELETE',
        url: `ai-studio/custom-flashbacks/${flashbackId}`,
      }),
    }),
    finalizeAIStudioAttachment: builder.mutation<Attachment, string>({
      query: attachmentId => ({
        method: 'POST',
        url: `ai-studio/attachments/${attachmentId}/finalize`,
      }),
    }),
    getAIStudioConversationMessages: builder.query<
      ConversationMessagesResponse,
      { excludeActiveRun?: boolean; threadId: string }
    >({
      query: ({ excludeActiveRun, threadId }) => ({
        params: excludeActiveRun ? { exclude_active_run: 'true' } : {},
        url: `ai-studio/conversations/${threadId}/messages`,
      }),
    }),
    getAIStudioConversations: builder.query<
      ConversationListResponse,
      { all?: boolean; cursor?: string; limit?: number } | void
    >({
      providesTags: ['AIStudioConversations'],
      query: params => ({
        params: {
          ...(params?.limit ? { limit: params.limit } : {}),
          ...(params?.cursor ? { cursor: params.cursor } : {}),
          ...(params?.all ? { all: 'true' } : {}),
        },
        url: 'ai-studio/conversations',
      }),
    }),
    getCustomFlashbacks: builder.query<
      CustomFlashbackListResponse,
      { cursor?: string; limit?: number } | void
    >({
      providesTags: ['CustomFlashbacks'],
      query: params => ({
        params: {
          limit: params?.limit ?? 20,
          ...(params?.cursor ? { cursor: params.cursor } : {}),
        },
        url: 'ai-studio/custom-flashbacks',
      }),
    }),
    getFlashbackCards: builder.query<PromptCardsResponse, void>({
      query: () => 'ai-studio/flashback-cards',
    }),
    getLatestCliRelease: builder.query<CliRelease, void>({
      keepUnusedDataFor: 0,
      query: () => 'ai-studio/cli/releases/latest',
    }),
    renameAIStudioConversation: builder.mutation<
      { status: string },
      { threadId: string; title: string }
    >({
      invalidatesTags: ['AIStudioConversations'],
      query: ({ threadId, title }) => ({
        body: { title },
        method: 'PATCH',
        url: `ai-studio/conversations/${threadId}`,
      }),
    }),
    reportAIStudioSession: builder.mutation<SessionReport, string>({
      query: conversationId => ({
        body: { conversation_id: conversationId },
        method: 'POST',
        url: 'ai-studio/session-reports',
      }),
    }),
    updateCustomFlashback: builder.mutation<
      CustomFlashback,
      {
        flashbackId: string;
        prompt?: string;
        subtitle?: string;
        title?: string;
      }
    >({
      invalidatesTags: ['CustomFlashbacks'],
      query: ({ flashbackId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `ai-studio/custom-flashbacks/${flashbackId}`,
      }),
    }),
  }),
});

export const {
  useCancelAIStudioRunMutation,
  useClearPendingAIStudioAttachmentsMutation,
  useCreateAIStudioAttachmentMutation,
  useCreateCustomFlashbackMutation,
  useDeleteAIStudioConversationMutation,
  useDeleteCustomFlashbackMutation,
  useFinalizeAIStudioAttachmentMutation,
  useGetAIStudioConversationsQuery,
  useGetCustomFlashbacksQuery,
  useGetFlashbackCardsQuery,
  useGetLatestCliReleaseQuery,
  useLazyGetAIStudioConversationMessagesQuery,
  useLazyGetAIStudioConversationsQuery,
  useLazyGetCustomFlashbacksQuery,
  useRenameAIStudioConversationMutation,
  useReportAIStudioSessionMutation,
  useUpdateCustomFlashbackMutation,
} = aiStudioApi;
